import { useState } from "react";
import * as XLSX from "xlsx";
import { ArrowUpTrayIcon, CheckCircleIcon, ExclamationCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { Button, Card, Container, FileUpload, InfoBox, PageHeader } from "./ui";

const EMPTY_RESULTS = { success: [], failed: [] };

// The first sheet's rows as objects keyed by the header row
export const parseExcelFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
        resolve(XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]]));
      } catch (err) {
        reject(new Error("Failed to parse Excel file"));
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsArrayBuffer(file);
  });

/**
 * Bulk import from an Excel sheet: every row is sent to `saveRecord(row)` one request at a
 * time, and the rows that fail are listed with their spreadsheet row number and the error.
 */
export default function SpreadsheetUpload({ title, subtitle, note, saveRecord }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [error, setError] = useState("");

  const handleFileChange = (selected) => {
    setResults(EMPTY_RESULTS);
    if (!selected) {
      setFile(null);
      setError("");
      return;
    }
    const extension = selected.name.split(".").pop().toLowerCase();
    if (["xlsx", "xls"].includes(extension)) {
      setFile(selected);
      setError("");
    } else {
      setFile(null);
      setError("Please upload a valid Excel file (.xlsx or .xls)");
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a file first");
      return;
    }
    setUploading(true);
    setError("");
    setResults(EMPTY_RESULTS);
    setProgress({ current: 0, total: 0 });

    try {
      const records = await parseExcelFile(file);
      if (!records?.length) throw new Error("No data found in Excel file");

      const success = [];
      const failed = [];
      setProgress({ current: 0, total: records.length });

      for (let i = 0; i < records.length; i++) {
        // Spreadsheet row number: data starts on row 2, under the header
        const row = i + 2;
        try {
          await saveRecord(records[i]);
          success.push({ row, data: records[i] });
        } catch (err) {
          console.error(`Failed to upload row ${row}:`, err);
          failed.push({ row, data: records[i], error: err.response?.data?.message || err.message });
        }
        setProgress({ current: i + 1, total: records.length });
      }

      setResults({ success, failed });
      if (success.length === 0) setError("All records failed to upload");
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const percent = progress.total ? Math.round((progress.current / progress.total) * 100) : 0;
  const hasResults = results.success.length > 0 || results.failed.length > 0;

  return (
    <Container className="flex flex-col gap-6 py-6">
      <PageHeader title={title} subtitle={subtitle} />

      <Card className="max-w-3xl" bodyClassName="flex flex-col gap-5">
        <InfoBox>{note}</InfoBox>

        <FileUpload
          label="Excel file"
          required
          accept=".xlsx,.xls"
          hint="XLSX or XLS"
          file={file}
          onChange={uploading ? () => {} : handleFileChange}
          error={error}
        />

        <Button fullWidth onClick={handleUpload} disabled={!file} loading={uploading} icon={<ArrowUpTrayIcon className="h-4 w-4" />}>
          {uploading ? `Processing ${progress.current} of ${progress.total}` : "Upload and process"}
        </Button>

        {uploading && progress.total > 0 && (
          <div className="flex flex-col gap-2">
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full bg-brand transition-all duration-300" style={{ width: `${percent}%` }} />
            </div>
            <p className="text-center text-sm text-slate-500">{percent}% complete</p>
          </div>
        )}

        {hasResults && (
          <div className="flex flex-col gap-3">
            <h2 className="text-lg font-semibold text-slate-900">Upload results</h2>
            {results.success.length > 0 && (
              <p className="flex items-center gap-2 rounded-lg bg-green-50 p-3 text-sm font-medium text-green-800">
                <CheckCircleIcon className="h-5 w-5 text-green-600" />
                Uploaded {results.success.length} record(s)
              </p>
            )}
            {results.failed.length > 0 && (
              <div className="rounded-lg bg-red-50 p-3">
                <p className="mb-2 flex items-center gap-2 text-sm font-medium text-red-800">
                  <XCircleIcon className="h-5 w-5 text-red-600" />
                  Failed to upload {results.failed.length} record(s)
                </p>
                <ul className="thin-scrollbar flex max-h-48 flex-col gap-2 overflow-y-auto">
                  {results.failed.map((fail) => (
                    <li key={fail.row} className="flex items-start gap-2 rounded-md bg-white p-2 text-sm text-red-700">
                      <ExclamationCircleIcon className="mt-0.5 h-4 w-4 shrink-0" />
                      <span>
                        <span className="font-medium">
                          Row {fail.row}: {fail.error}
                        </span>
                        <span className="block text-xs text-red-600">
                          {fail.data.firstName} {fail.data.lastName} · {fail.data.registrationNumber}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>
    </Container>
  );
}
